import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Microphone,
  StopCircle,
  PaperPlaneTilt,
  Spinner,
  CheckCircle,
  UserGear,
  ArrowLeft,
  Quotes,
  Compass,
  Sparkle,
  SignOut,
  FileText,
  Heart,
  Brain,
  ChatsCircle,
  Handshake,
  DotsThree,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import useRecorder from "../hooks/useRecorder";
import Disclaimer from "../components/Disclaimer";

// 4-stage framework. Stage keys must match the backend's current_stage values.
const STAGES = [
  { key: "calm", label: "Calm Yourself", icon: Heart },
  { key: "reflect", label: "Identify Needs", icon: Brain },
  { key: "express_listen", label: "Talk & Listen", icon: ChatsCircle },
  { key: "brainstorm", label: "Find Solutions", icon: Handshake },
];

function StageStepper({ currentStage, layout = "horizontal" }) {
  const currentIdx = Math.max(0, STAGES.findIndex((s) => s.key === currentStage));
  if (layout === "vertical") {
    return (
      <div data-testid="walk-stage-stepper" className="space-y-1">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const isCurrent = i === currentIdx;
          const isDone = i < currentIdx;
          return (
            <div
              key={s.key}
              data-testid={`stage-${s.key}`}
              data-current={isCurrent || undefined}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all ${
                isCurrent ? "bg-[#1E3A2B]/8" : ""
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                  isCurrent
                    ? "bg-[#1E3A2B] text-white ring-4 ring-[#1E3A2B]/15"
                    : isDone
                    ? "bg-[#1E3A2B]/15 text-[#1E3A2B]"
                    : "bg-[#F3F0EA] text-[#8A948E]"
                }`}
              >
                <Icon size={14} weight="duotone" />
              </div>
              <div
                className={`text-[11px] uppercase tracking-[0.1em] leading-tight ${
                  isCurrent
                    ? "text-[#1E3A2B] font-semibold"
                    : isDone
                    ? "text-[#1E3A2B]/70"
                    : "text-[#8A948E]"
                }`}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="bg-white/95 backdrop-blur border border-[#E8E4DB] rounded-2xl p-4 sm:p-5" data-testid="walk-stage-stepper">
      <div className="grid grid-cols-4 gap-2">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const isCurrent = i === currentIdx;
          const isDone = i < currentIdx;
          return (
            <div
              key={s.key}
              data-testid={`stage-${s.key}`}
              data-current={isCurrent || undefined}
              className="flex flex-col items-center text-center"
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  isCurrent
                    ? "bg-[#1E3A2B] text-white ring-4 ring-[#1E3A2B]/15"
                    : isDone
                    ? "bg-[#1E3A2B]/15 text-[#1E3A2B]"
                    : "bg-[#F3F0EA] text-[#8A948E]"
                }`}
              >
                <Icon size={16} weight="duotone" />
              </div>
              <div
                className={`mt-2 text-[10px] sm:text-xs uppercase tracking-[0.1em] leading-tight ${
                  isCurrent ? "text-[#1E3A2B] font-semibold" : isDone ? "text-[#1E3A2B]/70" : "text-[#8A948E]"
                }`}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssistantTurnCard({ turn, index, isLast, isQuick = false, onQuickReply }) {
  if (!turn) return null;

  // ——— Three Alternatives view (when "Explore more possibilities" was used) ———
  if (Array.isArray(turn.alternatives) && turn.alternatives.length > 0) {
    return (
      <div
        data-testid={`walk-assistant-turn-${index}`}
        className="bg-white border border-[#E8E4DB] rounded-2xl p-6 sm:p-8 shadow-sm"
      >
        <div className="flex items-center gap-2 label-kicker" style={{ color: "#1E3A2B" }}>
          <Sparkle size={14} weight="duotone" /> Three approaches to consider
        </div>
        <p className="mt-3 text-[#4A524C] leading-relaxed">{turn.reading}</p>

        <div className="mt-6 space-y-5">
          {turn.alternatives.map((alt, i) => (
            <div
              key={i}
              data-testid={`walk-alternative-${i}`}
              className={`rounded-xl p-5 ${
                i === 0 ? "bg-[#F3F0EA] border border-[#1E3A2B]/15" : "bg-white border border-[#E8E4DB]"
              }`}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[#1E3A2B] text-lg flex-shrink-0">{i + 1}.</span>
                <div className="flex-1">
                  <div className="font-serif text-lg text-[#1E3A2B] leading-snug">{alt.title}</div>
                  {i === 0 && (
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#768C7E] mt-0.5">
                      The move on the table
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-3 text-[#121815] leading-relaxed">{alt.move}</p>
              {alt.suggested_words && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 label-kicker">
                    <Quotes size={12} weight="duotone" /> Words you can use
                  </div>
                  <p className="mt-2 italic text-[#121815] leading-relaxed border-l-2 border-[#1E3A2B] pl-4">
                    {alt.suggested_words}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {turn.question && (
          <p className="mt-6 pt-6 border-t border-[#E8E4DB] text-sm text-[#4A524C]">{turn.question}</p>
        )}
      </div>
    );
  }

  // ——— Standard "Immediate Priority" view ———
  const stageLabel = STAGES.find((s) => s.key === turn.current_stage)?.label;
  const isBraceStep =
    turn.current_stage === "calm" &&
    typeof turn.priority === "string" &&
    /^Step\s+\d+\s+of\s+5/i.test(turn.priority);

  // Render simple *italic* markdown inside instructions
  const renderInline = (text) => {
    if (!text) return text;
    const parts = text.split(/(\*[^*]+\*)/g);
    return parts.map((p, i) =>
      /^\*[^*]+\*$/.test(p) ? (
        <em key={i}>{p.slice(1, -1)}</em>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      ),
    );
  };

  return (
    <div
      data-testid={`walk-assistant-turn-${index}`}
      className="bg-white border border-[#E8E4DB] rounded-2xl p-6 sm:p-8 shadow-sm"
    >
      {/* In quick mode we hide the stage kicker entirely — it surprises users who
          never saw the framework. In guided mode (with the stepper sidebar) the
          stage label gives helpful context, so we keep it there. */}
      {!isQuick && (
        <div className="flex items-center gap-2 label-kicker" style={{ color: "#1E3A2B" }}>
          <Compass size={14} weight="duotone" />
          {isBraceStep
            ? "Let's Seek Calmness through the BRACE Method"
            : stageLabel
            ? `${stageLabel} · Recommendation`
            : "Recommendation"}
        </div>
      )}
      {isBraceStep ? (
        <>
          {/* Show the 5-step preview FIRST, before the Step heading — only on Step 1 (the introduction) */}
          {/^Step\s+1\s+of\s+5/i.test(turn.priority) && (
            <div
              data-testid="walk-brace-overview"
              className="mt-4 bg-[#F3F0EA] border border-[#E8E4DB] rounded-xl px-4 py-3"
            >
              <div className="label-kicker mb-2" style={{ color: "#1E3A2B" }}>
                BRACE at a glance
              </div>
              <ul className="space-y-1 text-sm text-[#4A524C]">
                <li>
                  Step 1 — <strong><u>B</u></strong>reathe
                </li>
                <li>
                  Step 2 — <strong><u>R</u></strong>eflect
                </li>
                <li>
                  Step 3 — <strong><u>A</u></strong>ccept that this difficult situation is happening
                </li>
                <li>
                  Step 4 — <strong><u>C</u></strong>ompassion — for you, not the other person yet
                </li>
                <li>
                  Step 5 — <strong><u>E</u></strong>mpower yourself
                </li>
              </ul>
            </div>
          )}
          <p
            className="mt-4 text-xl sm:text-2xl text-[#1E3A2B] leading-snug font-bold tracking-tight"
            data-testid="walk-brace-step-heading"
          >
            {turn.priority}
          </p>
          {turn.best_move && (
            <p className="mt-3 text-[#121815] leading-relaxed">{renderInline(turn.best_move)}</p>
          )}
        </>
      ) : isQuick ? (
        // ——— Quick-mode standard view — unified typography, no bold, no italics ———
        <div className="space-y-4 text-[#121815] leading-relaxed">
          {turn.reading && (
            <p data-testid="walk-quick-summary">
              {(() => {
                const r = turn.reading.trim();
                if (!r) return r;
                // Avoid double-prefixing if the LLM already wrote a "sounds like" opener.
                if (/^(it\s+sounds\s+like|sounds\s+like|what\s+you[''']?re\s+saying|what\s+you\s+are\s+saying|it\s+seems)/i.test(r)) {
                  return r;
                }
                return `It sounds like ${r.charAt(0).toLowerCase()}${r.slice(1)}`;
              })()}
            </p>
          )}
          {stageLabel && !isBraceStep && (
            <p data-testid="walk-quick-stage-intro">
              This is an opportunity for you to {stageLabel.toLowerCase()}.
            </p>
          )}
          {turn.priority && <p>{turn.priority}</p>}
          {turn.best_move && turn.best_move !== turn.priority && (
            <p>{turn.best_move.replace(/\*([^*]+)\*/g, "$1")}</p>
          )}
          {turn.suggested_words && (
            <p>Suggested language: {turn.suggested_words}</p>
          )}
          {turn.what_to_listen_for && (
            <p>What to listen for: {turn.what_to_listen_for}</p>
          )}
          {turn.question && <p>{turn.question}</p>}
        </div>
      ) : (
        <>
          <p className="mt-3 text-xl text-[#1E3A2B] leading-snug font-medium">
            {turn.priority || turn.best_move}
          </p>
          {turn.priority && turn.best_move && turn.best_move !== turn.priority && (
            <p className="mt-3 text-[#121815] leading-relaxed">{renderInline(turn.best_move)}</p>
          )}
        </>
      )}

      {/* Suggested-language + What-to-listen-for blocks are only rendered for the
          guided / BRACE view here. Quick mode renders them inline above to avoid
          visual noise. */}
      {!isQuick && turn.suggested_words && (
        <div className="mt-6 pt-6 border-t border-[#E8E4DB]">
          <div className="flex items-center gap-2 label-kicker">
            <Quotes size={14} weight="duotone" /> Suggested language
          </div>
          <p className="mt-3 italic text-[#121815] leading-relaxed border-l-2 border-[#1E3A2B] pl-4">
            {turn.suggested_words}
          </p>
        </div>
      )}

      {!isQuick && turn.what_to_listen_for && (
        <div className="mt-5 pt-5 border-t border-[#E8E4DB]">
          <div className="flex items-center gap-2 label-kicker">
            <ChatsCircle size={14} weight="duotone" /> What to listen for
          </div>
          <p className="mt-2 text-[#4A524C] leading-relaxed">{turn.what_to_listen_for}</p>
        </div>
      )}

      {!isQuick && turn.question && (
        <p className="mt-3 text-[#1E3A2B] leading-relaxed italic">
          {turn.question}
        </p>
      )}

      {isLast && Array.isArray(turn.quick_replies) && turn.quick_replies.length > 0 && (
        <div
          className="mt-4 flex flex-wrap gap-2"
          data-testid={`walk-quick-replies-${index}`}
        >
          {turn.quick_replies.map((qr, i) => (
            <button
              key={i}
              onClick={() => onQuickReply && onQuickReply(qr)}
              data-testid={`walk-quick-reply-${i}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-[#1E3A2B]/8 text-[#1E3A2B] border border-[#1E3A2B]/20 hover:bg-[#1E3A2B] hover:text-white transition-colors"
            >
              {qr}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserBubble({ text, index }) {
  return (
    <div data-testid={`walk-user-turn-${index}`} className="flex justify-end">
      <div className="max-w-[85%] bg-[#1E3A2B] text-white rounded-2xl rounded-br-sm px-5 py-3.5">
        <div className="whitespace-pre-wrap leading-relaxed">{text}</div>
      </div>
    </div>
  );
}

export default function WalkthroughSession() {
  const { id } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [transcribing, setTranscribing] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const bottomRef = useRef(null);
  const taRef = useRef(null);
  const lastTurnRef = useRef(null);
  const { recording: mrRecording, start, stop } = useRecorder();
  const [liveRecording, setLiveRecording] = useState(false);
  const recording = mrRecording || liveRecording;

  // Auto-grow the message textarea up to ~10 rows
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = 240; // px ~ 10 rows
    el.style.height = Math.min(el.scrollHeight, max) + "px";
  }, [input]);

  const load = async () => {
    try {
      const { data } = await api.get(`/walkthrough/${id}`);
      setSession(data);
    } catch (e) {
      toast.error("Could not load walkthrough");
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  // Scroll the latest assistant card into view at the TOP of the viewport
  // so the user doesn't have to scroll up to see the new step.
  useEffect(() => {
    if (loading) return;
    if (lastTurnRef.current) {
      lastTurnRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [session?.turns?.length, loading]);

  const sendTurn = async (message, chosenIntent = "continue") => {
    const text = (message ?? input).trim();
    if (!text) {
      toast.error("Share a short message to continue.");
      return;
    }
    setLoading(true);
    // optimistic user bubble (hidden for chip-triggered intents)
    const showBubble = chosenIntent === "continue" && !message;
    if (showBubble) {
      setSession((s) => ({
        ...s,
        turns: [...(s?.turns || []), { role: "user", text, intent: chosenIntent }],
      }));
      setInput("");
    }
    try {
      const { data } = await api.post(`/walkthrough/${id}/turn`, {
        message: text,
        intent: chosenIntent,
      });
      setSession(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach turn failed");
      await load();
    } finally {
      setLoading(false);
    }
  };

  const handleTryAndReturn = () => {
    // Previously this injected a scripted user message ("I'm going to do this
    // now. Sharpen the script for me.") and fired another AI turn — which made
    // the assistant appear to fabricate the user's voice and then respond to
    // itself. The right behaviour is a graceful pause: the session is already
    // persisted server-side after every turn, so we just let the leader out.
    toast.success("Session saved. Good luck — come back and refine anytime.");
    nav("/");
  };

  const handleContactCoach = () => nav("/coach");

  const handleEndDiscussion = () => {
    toast.success("Walkthrough saved. You can return anytime.");
    nav("/");
  };

  const handleGetPlan = async () => {
    setGeneratingPlan(true);
    try {
      await api.post(`/walkthrough/${id}/plan`);
      nav(`/report/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate plan");
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleMic = async () => {
    // Prefer the browser's native SpeechRecognition for LIVE transcription —
    // words appear in the textarea as the user speaks, no upload, no LLM,
    // never leaves the device. Fall back to Whisper if unsupported.
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR && !window._sr_unsupported) {
      try {
        if (recording) {
          // stop the live recognition
          if (window._activeRecognition) window._activeRecognition.stop();
          return;
        }
        const recog = new SR();
        recog.continuous = true;
        recog.interimResults = true;
        recog.lang = navigator.language || "en-US";
        const baseRef = { value: input };
        recog.onresult = (evt) => {
          let finalChunk = "";
          let interim = "";
          for (let i = evt.resultIndex; i < evt.results.length; i++) {
            const t = evt.results[i][0].transcript;
            if (evt.results[i].isFinal) finalChunk += t;
            else interim += t;
          }
          if (finalChunk) {
            baseRef.value = (baseRef.value ? baseRef.value + " " : "") + finalChunk.trim();
          }
          const merged = baseRef.value + (interim ? (baseRef.value ? " " : "") + interim : "");
          setInput(merged);
        };
        recog.onerror = (e) => {
          console.warn("SpeechRecognition error", e);
          // fall back to upload-after path on next click
          window._sr_unsupported = true;
        };
        recog.onend = () => {
          window._activeRecognition = null;
          setLiveRecording(false);
        };
        window._activeRecognition = recog;
        setLiveRecording(true);
        recog.start();
        return;
      } catch (e) {
        console.warn("SpeechRecognition failed to start, falling back:", e);
        window._sr_unsupported = true;
      }
    }
    // ——— Fallback: legacy upload-after-recording via Whisper ———
    if (!recording) {
      await start();
      return;
    }
    const blob = await stop();
    if (!blob) return;
    setTranscribing(true);
    try {
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      const form = new FormData();
      form.append("audio", blob, `recording.${ext}`);
      const { data } = await api.post("/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInput((prev) => (prev ? prev + " " + data.text : data.text));
    } catch {
      toast.error("Could not transcribe.");
    } finally {
      setTranscribing(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center text-[#4A524C]">
        <Spinner size={28} className="animate-spin mx-auto" />
        <div className="mt-3">Loading your walkthrough…</div>
      </div>
    );
  }

  if (!session) return null;

  const CHOICES = [
    {
      key: "try",
      label: "Pause Session and Give It a Try",
      icon: CheckCircle,
      onClick: handleTryAndReturn,
    },
    {
      key: "live",
      label: "Bring in a Live Coach",
      icon: UserGear,
      onClick: handleContactCoach,
    },
    {
      key: "end",
      label: "End Session",
      icon: SignOut,
      onClick: () => setConfirmEnd(true),
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10 pb-16">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap anim-in delay-1">
        <button
          onClick={() => nav("/walkthrough")}
          data-testid="walkthrough-new-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#4A524C] hover:text-[#1E3A2B]"
        >
          <ArrowLeft size={16} weight="bold" /> New session
        </button>
        <div className="flex items-center gap-3">
          <div className="text-xs text-[#8A948E] font-mono">
            {new Date(session.created_at).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="mt-3 anim-in delay-2">
        <div className="label-kicker" style={session.mode === "quick" ? { color: "#C77D5E" } : undefined}>
          {session.mode === "quick" ? "Ask the Coach" : "Active engagement · Guided walkthrough"}
        </div>
      </div>

      {/* 2-column layout: sticky sidebar (desktop) + main thread */}
      <div className={`mt-6 ${session.mode !== "quick" ? "lg:grid lg:grid-cols-[200px_1fr] lg:gap-8" : ""}`}>
        {/* Sidebar — vertical stepper */}
        {session.mode !== "quick" && (
          <aside className="hidden lg:block">
            <div className="sticky top-6 anim-in delay-2">
              <div className="label-kicker mb-3">Progress</div>
              <StageStepper
                layout="vertical"
                currentStage={(() => {
                  const lastA = [...(session.turns || [])]
                    .reverse()
                    .find((t) => t.role === "assistant" && t.turn?.current_stage);
                  return lastA?.turn?.current_stage || "calm";
                })()}
              />
            </div>
          </aside>
        )}

        {/* Main column */}
        <div className="min-w-0">
          {/* Mobile-only horizontal stepper */}
          {session.mode !== "quick" && (
            <div className="lg:hidden mb-5 anim-in delay-2">
              <StageStepper
                layout="horizontal"
                currentStage={(() => {
                  const lastA = [...(session.turns || [])]
                    .reverse()
                    .find((t) => t.role === "assistant" && t.turn?.current_stage);
                  return lastA?.turn?.current_stage || "calm";
                })()}
              />
            </div>
          )}

          {/* Conversation */}
          <div className="space-y-5" data-testid="walkthrough-conversation">
            {session.turns.map((t, i) => {
              const lastAssistantIdx = [...session.turns]
                .map((x, idx) => (x.role === "assistant" ? idx : -1))
                .filter((idx) => idx !== -1)
                .pop();
              const isLast = i === lastAssistantIdx;
              return t.role === "user" ? (
                <UserBubble key={i} index={i} text={t.text} />
              ) : (
                <div key={i} ref={isLast ? lastTurnRef : null} className="scroll-mt-6">
                  <AssistantTurnCard
                    index={i}
                    turn={t.turn}
                    isLast={isLast}
                    isQuick={session.mode === "quick"}
                    onQuickReply={(text) => sendTurn(text, "continue")}
                  />
                </div>
              );
            })}
            {loading && (
              <div className="bg-white border border-[#E8E4DB] rounded-2xl p-6 inline-flex items-center gap-2 text-[#4A524C]">
                <Spinner size={18} className="animate-spin" /> Coach is with you…
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Session actions — always open, placed right under the most recent dialogue */}
          {!loading && (
            <details className="mt-6" data-testid="walk-choices" open>
              <summary
                className="cursor-pointer inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#4A524C] hover:text-[#1E3A2B]"
                style={session.mode === "quick" ? { color: "#C77D5E" } : undefined}
              >
                <DotsThree size={14} weight="bold" />
                Session actions
              </summary>
              <div
                className={`mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 ${
                  session.mode === "quick" ? "p-4 rounded-2xl bg-[#FAEFE8] border border-[#C77D5E]/30" : ""
                }`}
              >
                {CHOICES.map((c) => {
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.key}
                      onClick={c.onClick}
                      disabled={loading}
                      data-testid={`walk-choice-${c.key}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white text-[#121815] border border-[#E8E4DB] hover:border-[#1E3A2B] hover:shadow-sm transition-all disabled:opacity-60 text-left"
                    >
                      <Icon size={14} weight="duotone" color="#1E3A2B" />
                      <span className="flex-1">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </details>
          )}

          {/* Inline composer — sits directly below the most recent dialogue + session actions */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendTurn();
            }}
            className="mt-6"
          >
            <div className="bg-white border border-[#E8E4DB] rounded-2xl px-3 py-2 flex items-end gap-2 shadow-sm">
              <textarea
                data-testid="walk-input"
                ref={taRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendTurn();
                  }
                }}
                placeholder="Reply or ask a question…  (Shift+Enter for a new line)"
                className="flex-1 resize-none bg-transparent focus:outline-none p-2 text-base leading-relaxed text-[#121815] placeholder-[#8A948E] whitespace-pre-wrap break-words overflow-y-auto"
                style={{ minHeight: "44px", maxHeight: "200px", wordBreak: "break-word" }}
              />
              <button
                type="button"
                onClick={handleMic}
                disabled={transcribing}
                data-testid="walk-mic-button"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                  recording ? "mic-recording" : "bg-[#F3F0EA] text-[#1E3A2B] hover:bg-[#E8E4DB]"
                } disabled:opacity-50`}
              >
                {transcribing ? (
                  <Spinner size={18} className="animate-spin" />
                ) : recording ? (
                  <StopCircle size={20} weight="fill" />
                ) : (
                  <Microphone size={20} weight="fill" />
                )}
              </button>
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="walk-send-button"
                className="w-10 h-10 rounded-full bg-[#1E3A2B] text-white flex items-center justify-center hover:bg-[#2C4C3B] disabled:opacity-50 flex-shrink-0"
              >
                <PaperPlaneTilt size={18} weight="fill" />
              </button>
            </div>
            <Disclaimer variant="compact" className="mt-3 text-center" />
          </form>
        </div>
      </div>

      {/* End-discussion confirmation */}
      {confirmEnd && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50"
          onClick={() => setConfirmEnd(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-8 max-w-md w-full border border-[#E8E4DB]"
          >
            <h3 className="font-serif text-2xl text-[#1E3A2B]">End this walkthrough?</h3>
            <p className="text-sm text-[#4A524C] mt-3 leading-relaxed">
              Your conversation is saved. You can return to it later, or generate a
              fuller plan before you go.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => setConfirmEnd(false)}
                className="px-5 py-2.5 rounded-xl bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#121815]"
              >
                Keep going
              </button>
              <button
                onClick={() => {
                  setConfirmEnd(false);
                  handleGetPlan();
                }}
                data-testid="walk-end-get-plan"
                className="px-5 py-2.5 rounded-xl bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#121815] inline-flex items-center gap-2"
              >
                <FileText size={16} weight="duotone" /> Generate plan first
              </button>
              <button
                onClick={handleEndDiscussion}
                data-testid="walk-end-confirm"
                className="px-5 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B]"
              >
                End discussion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
